import { ThemeProvider } from "@highoutput/ui-components";
import type { NextPage } from "next";
import type { AppProps } from "next/app";
import Head from "next/head";
import Script from "next/script";
import NextProgress from "nextjs-progressbar";
import type { ReactElement, ReactNode } from "react";
import GeorgiaFont from "~/components/fonts/georgia";
import HelveticaNeueFont from "~/components/fonts/helvetica-neue";
import theme from "~/config/theme";

/* FONTS */
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/playfair-display/700.css";

type MyAppProps = AppProps & {
  Component: NextPage & {
    getLayout?: (page: ReactElement) => ReactNode;
  };
};

export default function MyApp({ Component, pageProps }: MyAppProps) {
  const withLayout = Component.getLayout ?? ((page) => page);

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>

      <GeorgiaFont />
      <HelveticaNeueFont />

      <Script
        src="https://www.googletagmanager.com/gtag/js?id=G-99Y2KJ5H32"
        strategy="afterInteractive"
      />
      <Script
        id="GoogleTagManagerScript"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){window.dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-99Y2KJ5H32');
          `,
        }}
      />

      <Script
        id="HelpScoutBeaconScript"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
              !function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){});
              window.Beacon('init', '8c768d87-0f42-46b9-9a1c-e0f1a6a9f6c5')
            `,
        }}
      />

      <ThemeProvider theme={theme}>
        <NextProgress
          color={theme.colors.primary.light}
          height={8}
          stopDelayMs={200}
          startPosition={0.4}
          showOnShallow={false}
          options={{
            showSpinner: false,
          }}
        />

        {withLayout(<Component {...pageProps} />)}
      </ThemeProvider>
    </>
  );
}
